import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import type { MenuItemData, SubMenuData } from '@/apis/types';
import {
  CrossIcon,
  HamburgerIcon,
  NotificationIcon,
} from '@/assets/icons/icons';
import { useSpin } from '@/components/SpinContext';
import { trackEvent } from '@/hooks';
import { BeaconEventType } from '@/common/enum';
import { useToken } from '@/common/helper';
import { lanePlannerEnabledForCompany } from '@/common/constants';
import type { AppTheme } from '@/assets/theme';
import NotificationDropdown from '@/pages/Notifications/NotificationDropdown';
import {
  createMobileMenuIconMap,
  renderMobileMenuIcon,
  type IconMapValue,
} from './MobileMenuIcons';
import { buildLocalActions } from './MobileMenuActions';

interface RootState {
  menu: { items: MenuItemData[] };
}

interface MobileMenuProps {
  logoutCall: () => void | Promise<void>;
  helpDropdown: React.ReactNode;
  helpOpen: boolean;
  onHelpOpenChange: (open: boolean) => void;
  // When set (uat/localhost), Help opens the Help center instead of the old menu.
  onOpenHelpCenter?: () => void;
  onOpenAiInsights: () => void;
  showAiInsightsChat: boolean;
  showMessages: boolean;
  theme: AppTheme;
}

const MobileMenu: React.FC<MobileMenuProps> = ({
  logoutCall,
  helpDropdown,
  helpOpen,
  onHelpOpenChange,
  onOpenHelpCenter,
  onOpenAiInsights,
  showAiInsightsChat,
  showMessages,
  theme,
}) => {
  const [open, setOpen] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { showSpin, hideSpin } = useSpin();

  const menuItems = useSelector((state: RootState) => state.menu.items);

  const { decodedUserToken } = useToken();
  const decodedToken = decodedUserToken();
  const companyId = decodedToken?.company_id;

  // Mirror CustomSidebar: inject Lane planner for the cargo.one pilot allowlist.
  const lanePlannerItem: MenuItemData | null = useMemo(
    () =>
      lanePlannerEnabledForCompany(companyId)
        ? {
            id: 'lane-planner',
            name: 'Lane planner',
            label: 'Lane planner',
            icon: 'LanePlannerIcon',
            coloredIcon: 'ColoredLanePlannerIcon',
            route: 'lane-planner',
            permissions: {
              is_create: true,
              is_update: true,
              is_delete: false,
              is_view: true,
            },
            sub_menus: [] as SubMenuData[],
          }
        : null,
    [companyId]
  );

  const [activeMenu, setActiveMenu] = useState<string>('dashboard');
  const [_reportModalOpen, setReportModalOpen] = useState(false);

  const iconMap = createMobileMenuIconMap();

  const visibleMenuItems = useMemo(() => {
    const filtered = menuItems.filter((item) => {
      const itemName = item.name?.toLowerCase();
      const itemLabel = item.label?.toLowerCase();
      const itemRoute = item.route?.toLowerCase();

      return (
        itemName !== 'chatbot' &&
        itemName !== 'logiblocks' &&
        itemLabel !== 'automations' &&
        itemRoute !== 'logiblocks'
      );
    });
    if (
      !lanePlannerItem ||
      filtered.some((item) => item.route === 'lane-planner')
    ) {
      return filtered;
    }
    const next = [...filtered];
    const shipmentsIndex = next.findIndex((item) => item.route === 'shipment');
    const settingsIndex = next.findIndex((item) => item.route === 'settings');
    const insertionIndex =
      shipmentsIndex !== -1
        ? shipmentsIndex + 1
        : settingsIndex !== -1
          ? settingsIndex
          : next.length;
    next.splice(insertionIndex, 0, lanePlannerItem);
    return next;
  }, [menuItems, lanePlannerItem]);

  const filteredMenuItems = useMemo(
    () =>
      visibleMenuItems.filter((item) => {
        const userRole = decodedToken?.role?.toLowerCase();
        const isAdminOrEditor = userRole === 'admin' || userRole === 'editor';

        const isChatbotRestricted =
          item.route === 'chatbot' &&
          decodedToken?.company_name?.toLowerCase() !== 'zoomlogi';

        return (
          isAdminOrEditor ||
          item.route === 'airport-list' ||
          !isChatbotRestricted
        );
      }),
    [visibleMenuItems, decodedToken?.role, decodedToken?.company_name]
  );

  const getActiveMenuFromLocation = useCallback(() => {
    const mobileMenuActive = (
      location.state as { mobileMenuActive?: string } | null
    )?.mobileMenuActive;

    if (
      location.pathname === '/settings/profile' &&
      mobileMenuActive === 'profile'
    ) {
      return 'profile';
    }

    const currentPath = location.pathname.split('/')[1];
    const activeItem = filteredMenuItems.find(
      (item) => item.route === currentPath
    );
    return activeItem?.route ?? 'dashboard';
  }, [location.pathname, location.state, filteredMenuItems]);

  const restoreActiveMenu = useCallback(() => {
    setActiveMenu(getActiveMenuFromLocation());
  }, [getActiveMenuFromLocation]);

  useEffect(() => {
    setActiveMenu(getActiveMenuFromLocation());
  }, [getActiveMenuFromLocation]);

  const closeFloatingDropdowns = () => {
    onHelpOpenChange(false);
    setMessagesOpen(false);
  };

  const handleSetActiveMenu = (menuItem: MenuItemData): void => {
    closeFloatingDropdowns();

    if (menuItem.route) {
      setActiveMenu(menuItem.route);
    }

    if (menuItem.is_route === false) {
      setReportModalOpen(true);
    } else {
      const _ = navigate(`/${menuItem.route}`, {
        state: { mobileMenuActive: menuItem.route },
      });
    }

    setOpen(false);
  };

  useEffect(() => {
    if (!helpOpen && activeMenu === 'help') {
      restoreActiveMenu();
    }

    if (!messagesOpen && activeMenu === 'messages') {
      restoreActiveMenu();
    }
  }, [helpOpen, messagesOpen, activeMenu, restoreActiveMenu]);

  useEffect(() => {
    if (!helpOpen && !messagesOpen) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [helpOpen, messagesOpen]);

  const getIcon = (
    route: string | undefined,
    defaultIcon: string | undefined,
    coloredIcon: string | undefined
  ): IconMapValue => {
    const iconName = activeMenu === route ? coloredIcon : defaultIcon;

    return (iconName && iconMap[iconName]) || iconMap.DashboardIcon;
  };

  // Active rows sit on a `theme.activeRouteBg` pill, white for most themes,
  // dark navy for Zoomlogi, so the label needs the same contrast.
  // theme.activeRouteBg/activeIcon are already gated to production vs.
  // local/uat inside resolveTheme() (see assets/theme.ts) — no pill means
  // the old design, so fall back to no forced label color.
  const getLabelColor = (isActive: boolean): string | undefined => {
    if (!theme.activeRouteBg) {
      return undefined;
    }
    return isActive ? theme.activeIcon || theme.color : theme.color;
  };

  const renderLocalActionContent = (
    action: {
      icon: React.ReactNode;
      label: string;
    },
    isActive: boolean
  ) => (
    <Flex align='center' gap={12}>
      <div
        className='mobile-menu-icon mobile-menu-action-icon'
        // eslint-disable-next-line react/forbid-dom-props -- colour comes from the theme and active state at run time
        style={
          theme.activeRouteBg ? { color: getLabelColor(isActive) } : undefined
        }
      >
        {typeof action.icon === 'string' ? (
          <img
            className='mobile-menu-local-icon'
            src={action.icon}
            alt=''
            aria-hidden='true'
          />
        ) : (
          action.icon
        )}
      </div>
      <span
        className='mobile-menu-label'
        // eslint-disable-next-line react/forbid-dom-props -- colour comes from the theme and active state at run time
        style={
          theme.activeRouteBg
            ? ({
                '--active-route-bg': getLabelColor(isActive),
              } as React.CSSProperties)
            : undefined
        }
      >
        {action.label}
      </span>
    </Flex>
  );

  const localActions = buildLocalActions({
    closeFloatingDropdowns,
    logoutCall,
    navigate,
    onHelpOpenChange,
    onOpenAiInsights,
    showAiInsightsChat,
  });

  const renderMobilePanelCloseButton = (onClick: () => void) => (
    <button
      type='button'
      className='mobile-menu-fullscreen-close'
      onClick={onClick}
      aria-label='Close'
    >
      <CrossIcon size={16} color='var(--text-primary)' />
    </button>
  );

  return (
    <>
      <div className='mobile-header'>
        <button className='mobile-menu-toggle' onClick={() => setOpen(!open)}>
          {open ? (
            <CrossIcon size={20} color='#fff' />
          ) : (
            <HamburgerIcon width={22} height={22} />
          )}
        </button>
      </div>

      <div className={`mobile-menu-panel ${open ? 'open' : ''}`}>
        <ul className='mobile-menu-list'>
          {filteredMenuItems.map((item, index) => {
            const isLastItem = index === filteredMenuItems.length - 1;
            // Settings is handled via localActions (Profile/Logout). Only omit
            // that footer row — never drop Lane planner or a lone menu item.
            if (isLastItem && item.route === 'settings') {
              return null;
            }
            const isItemActive = activeMenu === item.route;
            return (
              <React.Fragment key={item.route}>
                <li
                  className={isItemActive ? 'active' : ''}
                  onClick={() => handleSetActiveMenu(item)}
                  // eslint-disable-next-line react/forbid-dom-props -- pill colour comes from the active theme at run time
                  style={
                    theme.activeRouteBg
                      ? ({
                          '--active-route-bg': theme.activeRouteBg,
                        } as React.CSSProperties)
                      : undefined
                  }
                >
                  <Flex align='center' gap={12}>
                    <div className='mobile-menu-icon'>
                      {(() => {
                        const icon = getIcon(
                          item.route,
                          item.icon,
                          item.coloredIcon
                        );

                        return renderMobileMenuIcon(icon);
                      })()}
                    </div>

                    <span
                      className='mobile-menu-label'
                      // eslint-disable-next-line react/forbid-dom-props -- colour comes from the theme and active state at run time
                      style={
                        theme.activeRouteBg
                          ? ({
                              '--active-route-bg': getLabelColor(isItemActive),
                            } as React.CSSProperties)
                          : undefined
                      }
                    >
                      {item.name === 'Shipment' ? 'Shipments' : item.name}
                    </span>
                  </Flex>
                </li>
              </React.Fragment>
            );
          })}

          {localActions.map((action) => {
            const localActionStyle = theme.activeRouteBg
              ? ({
                  '--active-route-bg': theme.activeRouteBg,
                } as React.CSSProperties)
              : undefined;

            if (action.key === 'help') {
              const isHelpActive = helpOpen || activeMenu === action.route;
              const isMessagesActive =
                messagesOpen || activeMenu === 'messages';
              return (
                <React.Fragment key={action.key}>
                  <li
                    className={isHelpActive ? 'active' : ''}
                    onClick={() => {
                      setMessagesOpen(false);
                      if (onOpenHelpCenter) {
                        // Close the menu so the Help center isn't under it.
                        setOpen(false);
                        onOpenHelpCenter();
                        return;
                      }
                      setActiveMenu(action.route);
                      onHelpOpenChange(true);
                    }}
                    // eslint-disable-next-line react/forbid-dom-props -- pill colour comes from the active theme at run time
                    style={localActionStyle}
                  >
                    {renderLocalActionContent(action, isHelpActive)}
                  </li>

                  {showMessages && (
                    <li
                      className={isMessagesActive ? 'active' : ''}
                      onClick={() => {
                        if (!messagesOpen) {
                          trackEvent(BeaconEventType.MessagesOpened);
                        }
                        onHelpOpenChange(false);
                        setActiveMenu('messages');
                        setMessagesOpen(true);
                      }}
                      // eslint-disable-next-line react/forbid-dom-props -- pill colour comes from the active theme at run time
                      style={localActionStyle}
                    >
                      {renderLocalActionContent(
                        {
                          icon: <NotificationIcon />,
                          label: 'Updates',
                        },
                        isMessagesActive
                      )}
                    </li>
                  )}
                </React.Fragment>
              );
            }

            const isActionActive = activeMenu === action.route;
            return (
              <li
                key={action.key}
                className={isActionActive ? 'active' : ''}
                onClick={() => {
                  closeFloatingDropdowns();
                  setActiveMenu(action.route);
                  action.onClick();
                  setOpen(false);
                }}
                // eslint-disable-next-line react/forbid-dom-props -- pill colour comes from the active theme at run time
                style={localActionStyle}
              >
                {renderLocalActionContent(action, isActionActive)}
              </li>
            );
          })}
        </ul>
      </div>

      {helpOpen && (
        <div className='mobile-menu-fullscreen-panel'>
          <div className='mobile-menu-fullscreen-header'>
            <span>Help</span>
            {renderMobilePanelCloseButton(() => {
              onHelpOpenChange(false);
              restoreActiveMenu();
            })}
          </div>
          <div className='mobile-menu-fullscreen-body mobile-menu-help-body'>
            {helpDropdown}
          </div>
        </div>
      )}

      {messagesOpen && (
        <div className='mobile-menu-fullscreen-panel'>
          <NotificationDropdown
            contentOnly
            useGlobalSpin
            showSpin={showSpin}
            hideSpin={hideSpin}
            onAction={() => {
              setMessagesOpen(false);
              restoreActiveMenu();
              setOpen(false);
            }}
            headerExtra={renderMobilePanelCloseButton(() => {
              setMessagesOpen(false);
              restoreActiveMenu();
            })}
          />
        </div>
      )}
    </>
  );
};

export default MobileMenu;
